<!-- TableView.vue -->
<template>
  <v-card
    class="d-flex flex-column"
    style="height: calc(100vh - 80px); overflow: hidden"
  >
    <!-- Barra de búsqueda múltiple -->
    <div class="search-container">
      <v-text-field
        v-model="searchDispositivo"
        label="Buscar Dispositivo"
        single-line
        hide-details
        dense
        outlined
        @input="handleSearch"
      ></v-text-field>
      <v-text-field
        v-model="searchLector"
        label="Buscar Lector"
        single-line
        hide-details
        dense
        outlined
        @input="handleSearch"
      ></v-text-field>
      <v-text-field
        v-model="searchTime"
        label="Buscar Timestamp"
        single-line
        hide-details
        dense
        outlined
        @input="handleSearch"
      ></v-text-field>
      <v-select
        v-model="searchEstado"
        :items="['Parado', 'En movimiento', 'Impacto']"
        label="Buscar Estado"
        hide-details
        dense
        outlined
        clearable
        @update:modelValue="handleSearch"
      ></v-select>
      <v-btn
        color="primary"
        dark
        small
        class="mt-2"
        @click="clearFilters"
        :disabled="!hasActiveFilters"
      >
        <v-icon left>mdi-filter-remove</v-icon>
        Limpiar Filtros
      </v-btn>
    </div>

    <!-- Barra de progreso -->
    <v-progress-linear
      v-if="tableStore.loading"
      indeterminate
      color="primary"
    ></v-progress-linear>

    <!-- Tabla de datos -->
    <v-data-table
      v-else
      :headers="computedHeaders"
      :items="tableStore.data"
      class="elevation-1"
      v-model:items-per-page="tableStore.perPage"
      :page="tableStore.tablePage"
      :server-items-length="tableStore.total"
      hide-default-footer
      fixed-header
    >
      <!-- Encabezado de la tabla -->
      <template #top>
        <v-toolbar flat>
          <v-toolbar-title>Inventario de Dispositivos</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            dark
            @click="reloadData"
            small
            style="margin-right: 8px"
          >
            <v-icon left>mdi-refresh</v-icon>
            Recargar
          </v-btn>
        </v-toolbar>
      </template>
      <template #no-data>
        <v-alert type="info" class="ma-4" border="left" colored-border>
          <div class="d-flex flex-column align-center">
            <div class="mb-2">
              <v-icon left>mdi-magnify-close</v-icon>
              No se encontraron eventos con esos filtros
            </div>
            <v-btn color="primary" small @click="clearFilters">
              <v-icon left>mdi-filter-remove</v-icon>
              Limpiar Filtros
            </v-btn>
          </div>
        </v-alert>
      </template>
      <!-- Renderización de cada fila usando computedHeaders -->
      <template #item="{ item }">
        <tr>
          <td v-for="header in computedHeaders" :key="header.value">
            <!-- Columna Estado -->
            <template v-if="header.value === 'Estado'">
              <span v-if="item.Estado === 'Impacto'">
                <v-icon color="red" class="mr-1">mdi-alert</v-icon>
                Impacto
              </span>
              <span v-else-if="item.Estado === 'En movimiento'">
                <v-icon color="blue" class="mr-1">mdi-run</v-icon>
                En movimiento
              </span>
              <span v-else-if="item.Estado === 'Parado'">
                <v-icon color="grey" class="mr-1">mdi-stop-circle</v-icon>
                Parado
              </span>
              <span v-else>
                {{ item.Estado }}
              </span>
            </template>
            <!-- Columna Etiqueta -->
            <template v-else-if="header.value === 'Etiqueta'">
              <span v-if="item.Etiqueta === '-1'">
                <v-icon color="red" class="mr-1">mdi-tag-off</v-icon>
                {{ item.Etiqueta }}
              </span>
              <span v-else>
                <v-icon color="green" class="mr-1">mdi-tag</v-icon>
                {{ item.Etiqueta }}
              </span>
            </template>
            <!-- Columna Batería -->
            <template v-else-if="header.value === 'Bateria'">
              <v-progress-linear
                v-if="item.Bateria !== null"
                :model-value="item.Bateria"
                height="16"
                :color="
                  item.Bateria >= 99.5 ? 'lime' : batteryColor(item.Bateria)
                "
                rounded
                striped
                style="
                  transition: width 2s ease-in-out,
                    background-color 0.5s ease-in-out;
                "
              >
                <template v-slot:default>
                  <small>
                    {{
                      item.Bateria !== null
                        ? parseFloat(item.Bateria).toFixed(2) + "%"
                        : "Sin datos"
                    }}
                  </small>
                </template>
              </v-progress-linear>
            </template>
            <!-- Columna Temperatura -->
            <template v-else-if="header.value === 'temperatura'">
              <span>
                <template v-if="parseFloat(item.temperatura) < 0">
                  <v-icon color="blue" class="mr-1">mdi-snowflake-alert</v-icon>
                </template>
                <template v-else-if="parseFloat(item.temperatura) < 15">
                  <v-icon color="light-blue" class="mr-1"
                    >mdi-thermometer-low</v-icon
                  >
                </template>
                <template v-else-if="parseFloat(item.temperatura) < 25">
                  <v-icon color="green" class="mr-1">mdi-thermometer</v-icon>
                </template>
                <template v-else-if="parseFloat(item.temperatura) < 35">
                  <v-icon color="orange" class="mr-1"
                    >mdi-thermometer-high</v-icon
                  >
                </template>
                <template v-else>
                  <v-icon color="red" class="mr-1"
                    >mdi-thermometer-alert</v-icon
                  >
                </template>
                {{ parseFloat(item.temperatura).toFixed(2) }}°C
              </span>
            </template>
            <!-- Columna Apodo Dispositivo -->
            <template v-else-if="header.value === 'ApodoDispositivo'">
              <v-chip
                color="primary"
                text-color="white"
                @click="goToInventory(item.ApodoDispositivo)"
                style="cursor: pointer"
                small
                class="ma-1"
              >
                <v-icon left small>mdi-bluetooth-connect</v-icon>
                {{ item.ApodoDispositivo }}
              </v-chip>
            </template>
            <!-- Apodo Lector -->
            <template v-else-if="header.value === 'ApodoLector'">
              <v-chip color="blue" text-color="white" small class="ma-1">
                <v-icon left small>mdi-access-point-network</v-icon>
                {{ item.ApodoLector }}
              </v-chip>
            </template>
            <!-- Columna Timestamp -->
            <template v-else-if="header.value === 'Time'">
              <v-icon color="primary" class="mr-1">mdi-clock-outline</v-icon>
              {{ item.Time }}
            </template>
            <!-- Otras columnas -->
            <template v-else>
              {{ item[header.value] }}
            </template>
          </td>
        </tr>
      </template>

      <!-- Footer personalizado -->
      <template #bottom>
        <!-- Layout para pantallas pequeñas -->
        <div class="d-flex d-md-none flex-column w-100 pa-2">
          <v-row class="w-100 d-flex align-center justify-start mb-2 flex-wrap">
            <v-col cols="12" class="mb-2">
              <v-select
                v-model="tableStore.perPage"
                :items="itemsPerPageOptions"
                label="Filas por página"
                hide-details
                dense
                outlined
                style="max-width: 150px"
              ></v-select>
            </v-col>
            <v-col cols="12">
              <v-pagination
                v-model="tableStore.actualPage"
                :length="tableStore.totalPages"
                total-visible="2"
              ></v-pagination>
            </v-col>
          </v-row>
          <v-row class="w-100 d-flex align-center justify-space-between">
            <v-col cols="auto" class="d-flex align-center">
              <span>Total: {{ tableStore.total }}</span>
            </v-col>
            <v-col cols="auto" class="d-flex justify-end">
              <v-select
                v-model="selectedColumnValues"
                :items="allHeaders"
                item-title="title"
                item-value="value"
                label="Columnas"
                multiple
                dense
                outlined
                hide-details
                style="max-width: 300px; margin-right: 8px"
              >
                <template #selection="{ index }">
                  <span v-if="index === 0">Columnas</span>
                </template>
              </v-select>
              <v-date-input
                v-model="fechaInicio"
                label="Fecha inicio"
                prepend-icon="mdi-calendar"
                density="comfortable"
                hide-details
                style="width: 240px; margin-right: 8px"
              />
              <v-date-input
                v-model="fechaFin"
                label="Fecha fin"
                prepend-icon="mdi-calendar"
                density="comfortable"
                hide-details
                style="width: 240px; margin-right: 16px"
              />
              <v-btn
                color="green"
                dark
                @click="exportarExcelCompleto"
                small
                style="margin-right: 12px"
              >
                <v-icon left>mdi-database-export</v-icon>
                Exportar TODO
              </v-btn>
              <v-btn
                color="success"
                dark
                @click="exportarExcel"
                small
                aria-label="Exportar a Excel"
              >
                <v-icon left>mdi-file-excel</v-icon>
                Exportar a Excel
              </v-btn>
              <v-btn
                color="red"
                dark
                @click="exportarPDF"
                small
                style="margin-left: 8px"
              >
                <v-icon left>mdi-file-pdf-box</v-icon>
                Exportar a PDF
              </v-btn>
            </v-col>
          </v-row>
        </div>

        <!-- Layout para pantallas grandes -->
        <div
          class="d-none d-md-flex w-100 pa-2 align-center justify-space-between flex-wrap"
        >
          <div class="d-flex align-center flex-wrap">
            <v-select
              v-model="tableStore.perPage"
              :items="itemsPerPageOptions"
              label="Filas por página"
              hide-details
              dense
              outlined
              style="max-width: 150px"
              class="mr-4"
            ></v-select>
            <v-pagination
              v-model="tableStore.actualPage"
              :length="tableStore.totalPages"
              total-visible="2"
              class="mr-4"
            ></v-pagination>
            <span>Total: {{ tableStore.total }}</span>
          </div>
          <div class="d-flex">
            <v-select
              v-model="selectedColumnValues"
              :items="allHeaders"
              item-title="title"
              item-value="value"
              multiple
              dense
              outlined
              hide-details
              style="max-width: 300px; margin-right: 8px"
            >
              <template #selection="{ index }">
                <span v-if="index === 0">Columnas</span>
              </template>
            </v-select>
            <v-date-input
              v-model="fechaInicio"
              label="Fecha inicio"
              prepend-icon="mdi-calendar"
              density="comfortable"
              hide-details
              style="width: 240px; margin-right: 8px"
            />
            <v-date-input
              v-model="fechaFin"
              label="Fecha fin"
              prepend-icon="mdi-calendar"
              density="comfortable"
              hide-details
              style="width: 240px; margin-right: 16px"
            />
            <v-btn
              color="green"
              dark
              @click="exportarExcelCompleto"
              small
              style="margin-right: 12px"
            >
              <v-icon left>mdi-database-export</v-icon>
              Exportar TODO
            </v-btn>
            <v-btn
              color="success"
              dark
              @click="exportarExcel"
              small
              aria-label="Exportar a Excel"
            >
              <v-icon left>mdi-file-excel</v-icon>
              Exportar a Excel
            </v-btn>
            <v-btn
              color="red"
              dark
              @click="exportarPDF"
              small
              style="margin-left: 8px"
            >
              <v-icon left>mdi-file-pdf-box</v-icon>
              Exportar a PDF
            </v-btn>
          </div>
        </div>
      </template>
    </v-data-table>

    <v-alert v-if="tableStore.error" type="error" dismissible>
      {{ tableStore.error }}
    </v-alert>
  </v-card>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useTableStore } from "@/stores/tableStore";
import api from "@/services/api";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import debounce from "lodash/debounce";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useRouter } from "vue-router";
import { useRoute } from "vue-router";

const router = useRouter();
const route = useRoute();

const tableStore = useTableStore();

// Variables para los filtros
const searchDispositivo = ref("");
const searchLector = ref("");
const searchTime = ref("");
const searchEstado = ref("");

// Variables para las fechas
const fechaInicio = ref(null);
const fechaFin = ref(null);

// Función de búsqueda con debounce
const handleSearch = debounce(() => {
  tableStore.filterSearch = searchDispositivo.value;
  tableStore.filterApodoLector = searchLector.value;
  tableStore.filterTimestamp = searchTime.value;
  tableStore.filterEstado = searchEstado.value;
  tableStore.actualPage = 1;
  tableStore.fetchTableData();
}, 300);

// Función para recargar datos
const reloadData = async () => {
  console.log(
    "Filtros antes del fetch:",
    tableStore.filterSearch,
    tableStore.filterApodoLector,
    tableStore.filterTimestamp
  );
  await tableStore.fetchTableData();
  console.log(
    "Filtros tras fetch:",
    tableStore.filterSearch,
    tableStore.filterApodoLector,
    tableStore.filterTimestamp,
    tableStore.filterEstado
  );
};

const hasActiveFilters = computed(() => {
  return (
    searchDispositivo.value ||
    searchLector.value ||
    searchTime.value ||
    searchEstado.value
  );
});

function clearFilters() {
  searchDispositivo.value = "";
  tableStore.filterSearch = "";
  searchLector.value = "";
  tableStore.filterApodoLector = "";
  searchTime.value = "";
  tableStore.filterEstado = "";
  searchEstado.value = "";
  tableStore.filterTimestamp = "";
  tableStore.actualPage = 1;
  reloadData();
}

// Función para determinar el color de la batería
function batteryColor(battery) {
  if (battery >= 50) return "success"; // verde
  else if (battery >= 25) return "warning"; // amarillo
  else return "error"; // rojo
}

// Función para navegar a la vista de inventario
function goToInventory(apodo) {
  router.push({ name: "PocStatus", query: { apodo } });
}

onMounted(async () => {
  if (route.query.search) {
    searchDispositivo.value = route.query.search;
    handleSearch();
    reloadData();
  } else {
    await tableStore.fetchTableData();
  }
  console.log("Datos del Store:", tableStore.data);
});

// Definición de columnas
const baseHeaders = [
  { title: "Apodo Dispositivo", value: "ApodoDispositivo", sortable: true },
  { title: "Apodo Lector", value: "ApodoLector", sortable: true },
  //{ title: "Temperatura", value: "temperatura", sortable: true },
  { title: "Batería", value: "Bateria", sortable: true },
  { title: "Timestamp", value: "Time", sortable: true },
  { title: "Estado", value: "Estado", sortable: true },
  { title: "Etiqueta", value: "Etiqueta", sortable: true },
];

const allHeaders = ref([...baseHeaders]);

// Define el orden y columnas que deseas mostrar inicialmente
const defaultColumns = [
  "Time",
  "Estado",
  "Bateria",
  "ApodoDispositivo",
  "ApodoLector",
  "Etiqueta",
];

const selectedColumnValues = ref([...defaultColumns]);

watch(
  () => tableStore.data,
  (newData) => {
    if (!newData) return;
    const additionalKeys = new Set();
    newData.forEach((row) => {
      Object.keys(row).forEach((key) => {
        const exists = allHeaders.value.some((h) => h.value === key);
        if (!exists) {
          additionalKeys.add(key);
        }
      });
    });
    additionalKeys.forEach((key) => {
      const newHeader = {
        title: key.charAt(0).toUpperCase() + key.slice(1),
        value: key,
        sortable: true,
      };
      allHeaders.value.push(newHeader);
      if (
        defaultColumns.includes(key) &&
        !selectedColumnValues.value.includes(key)
      ) {
        selectedColumnValues.value.push(key);
      }
    });
  },
  { immediate: true, deep: true }
);

// Ordena los headers según el orden definido en selectedColumnValues
const computedHeaders = computed(() => {
  return allHeaders.value
    .filter((header) => selectedColumnValues.value.includes(header.value))
    .sort(
      (a, b) =>
        selectedColumnValues.value.indexOf(a.value) -
        selectedColumnValues.value.indexOf(b.value)
    );
});

const itemsPerPageOptions = [
  { title: "10", value: 10 },
  { title: "20", value: 20 },
  { title: "50", value: 50 },
  { title: "100", value: 100 },
  { title: "500", value: 500 },
  { title: "1000", value: 1000 },
];

// Observa los cambios en la paginación
watch(
  () => [tableStore.actualPage, tableStore.perPage],
  () => {
    tableStore.fetchTableData();
  },
  { immediate: true }
);

// Función para exportar a Excel
const exportarExcel = async () => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Eventos");
  worksheet.columns = computedHeaders.value.map((header) => ({
    header: header.title,
    key: header.value,
    width: 20,
  }));
  tableStore.data.forEach((row) => {
    const rowData = {};
    computedHeaders.value.forEach((h) => {
      rowData[h.value] = row[h.value];
    });
    worksheet.addRow(rowData);
  });
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), "eventos.xlsx");
};

// Función para exportar a PDF
const exportarPDF = () => {
  const doc = new jsPDF();

  doc.setFontSize(18);
  doc.text("Eventos Registrados", 14, 22);

  const columns = computedHeaders.value.map((header) => ({
    header: header.title,
    dataKey: header.value,
  }));

  const rows = tableStore.data.map((item) => {
    const rowData = {};
    columns.forEach((col) => {
      rowData[col.dataKey] = item[col.dataKey] || "";
    });
    return rowData;
  });

  autoTable(doc, {
    startY: 30,
    head: [columns.map((col) => col.header)],
    body: rows.map((row) => columns.map((col) => row[col.dataKey])),
    theme: "grid",
    headStyles: { fillColor: [33, 150, 243] }, // azul bonito
    alternateRowStyles: { fillColor: [240, 240, 240] },
    styles: { fontSize: 8 },
    margin: { top: 30 },
  });

  doc.save("eventos.pdf");
};

const formatFecha = (fecha) => {
  if (!fecha) return "";
  const yyyy = fecha.getFullYear();
  const mm = String(fecha.getMonth() + 1).padStart(2, "0");
  const dd = String(fecha.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const exportarExcelCompleto = async () => {
  try {
    const filtros = {
      search: tableStore.filterSearch,
      apodoLector: tableStore.filterApodoLector,
      timestamp: tableStore.filterTimestamp,
      estado: tableStore.filterEstado,
      start_date: formatFecha(fechaInicio.value),
      end_date: formatFecha(fechaFin.value),
    };

    if (!filtros.start_date && !filtros.end_date) {
      const confirmar = confirm(
        "No has seleccionado un rango de fechas. ¿Deseas exportar TODOS los registros? Esto puede tardar bastante."
      );
      if (!confirmar) return;
    }

    console.log("Exportando con filtros:", filtros);

    await api.downloadAllEventosExcel(filtros);
  } catch (e) {
    console.error("Error al exportar todos los eventos:", e);
  }
};
</script>

<style>
.elevation-1 {
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.12), 0px 1px 2px rgba(0, 0, 0, 0.24);
}

.pa-2 {
  padding: 0.5rem;
}

@media (max-width: 600px) {
  .text-right {
    text-align: center !important;
  }
  .mr-4 {
    margin-right: 0 !important;
    margin-bottom: 0.5rem !important;
  }
}

.v-data-table {
  overflow: auto;
}

.search-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin: 10px 0;
}
</style>
